import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Android from '@mui/icons-material/Android';
import EastIcon from '@mui/icons-material/East';

import { Capacitor } from '@capacitor/core';

import { ReactComponent as AppleIcon } from '../../../assets/icons/apple.svg';
import { ReactComponent as UpdateIcon } from '../../../assets/icons/update.svg';
import config from '../../../envConfig';
import { useAppFile } from '../../../hooks/use-app-file';
import { usePwaInstall } from '../../../hooks/use-pwa-install';
import {
  useGetAppLinkUpdate,
  useGetAppVariables,
  useGetVersion,
} from '../../../redux/api/appConfigApi';
import {
  AppVariableKeys,
  getAppVariableValueInsensitive,
} from '../../../types/interfaces/app-variable';
import { isValidHttpUrl } from '../../../utils/platform/is-valid-http-url';
import { openExternalUrl } from '../../../utils/platform/open-external-url';
import AppButton from '../../designe/buttons/app-button/AppButton';
import AppDialog from '../app-dialog/AppDialog';
import { useStyles } from './VersionUpdateDialog.styles';

interface IVersionUpdateDialogProps {
  open: boolean;
  onClose: () => void;
}

const VersionUpdateDialog = (props: IVersionUpdateDialogProps) => {
  const { open, onClose } = props;
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { data: appLinkData } = useGetAppLinkUpdate();
  const { data: versionInfo } = useGetVersion();
  const { data: variables } = useGetAppVariables();
  const { downloadFile, isDownloading } = useAppFile(t);
  const { isPwaInstalled, reloadPwa } = usePwaInstall();
  const apkFileName =
    getAppVariableValueInsensitive(variables, AppVariableKeys.apkFileName) ??
    'SportGround.apk';
  const iosBuildLink = getAppVariableValueInsensitive(
    variables,
    AppVariableKeys.iosBuildLink,
  );
  const hasValidIosBuildLink = isValidHttpUrl(iosBuildLink);
  const isNativePlatform = Capacitor.getPlatform() !== 'web';
  const displayedCurrentVersion = isNativePlatform
    ? config.version
    : versionInfo?.currentVersion;
  const backendVersion = versionInfo?.currentVersion;

  return (
    <AppDialog open={open} onClose={onClose} title={t('versionUpdate.title')}>
      <Box className={classes.dialogContent}>
        <Box className={classes.iconBadge}>
          <UpdateIcon className={classes.topIcon} />
        </Box>
        <Typography variant="h2">{t('versionUpdate.title')}</Typography>
        <Typography variant="text-md" className={classes.description}>
          {t('versionUpdate.description')}
        </Typography>
        <Box className={classes.versionCompareRow}>
          <Box className={classes.versionBadgeSecondary}>
            <Typography
              variant="text-md-sb"
              color="inherit"
              className={classes.versionValue}>
              {displayedCurrentVersion ?? '—'}
            </Typography>
          </Box>
          <EastIcon className={classes.versionArrow} aria-hidden />
          <Box className={classes.versionBadgePrimary}>
            <Typography
              variant="text-md-sb"
              color="inherit"
              className={classes.versionValue}>
              {backendVersion ?? '—'}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.actions}>
          {appLinkData?.link && (
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              className={classes.updateButton}
              loading={isDownloading}
              icon={<Android className={classes.buttonIcon} />}
              onClick={() =>
                downloadFile(appLinkData.link, apkFileName, {
                  successMessageKey: 'messages.fileDownloaded',
                })
              }>
              {t('versionUpdate.downloadUpdate')}
            </AppButton>
          )}
          {hasValidIosBuildLink && (
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              className={classes.updateButton}
              icon={<AppleIcon className={classes.buttonIcon} />}
              onClick={() => void openExternalUrl(iosBuildLink)}>
              {t('versionUpdate.openIosBuild')}
            </AppButton>
          )}
          {isPwaInstalled && (
            <AppButton
              variant="contained"
              color="primary"
              fullWidth
              className={classes.updateButton}
              onClick={reloadPwa}>
              {t('versionUpdate.updatePwa')}
            </AppButton>
          )}
          <AppButton
            variant="outlined"
            color="primary"
            fullWidth
            className={classes.closeButton}
            onClick={onClose}>
            {t('common.close')}
          </AppButton>
        </Box>
      </Box>
    </AppDialog>
  );
};

export default VersionUpdateDialog;
